/**
 * @author: Corey Sanford <corey@hackcapital.com>
 * @date:   2019-01-04 10:14:38
 *
 * Given some conditions and value for a caret delimited field,
 * construct a feathers query object.
 */

import { QueryBuilderBase } from './QueryBuilderBase';

export class CaretDelimitedStringQueryBuilder extends QueryBuilderBase {

  static get Operators() {
    return {
      CONTAINS_ANY: { name: 'include any of', needValue: true }, // formerly "contain any of"
      CONTAINS_ALL: { name: 'include all of', needValue: true }, // formerly "contain all of"
      DOES_NOT_CONTAIN_ANY: { name: 'does not include any of', needValue: true },
      HAS_NONE: { name: 'are empty', needValue: false },
      HAS_ANY: { name: 'are not empty', needValue: false },
    };
  }

  /**
   * Given some operator, create the query object for feathers
   *
   * @param  {string} fieldName
   * @param  {array} operands - either single value or array
   * @return {object} the query object
   */

  buildCondition(fieldName, operands, operator) {
    let condition = {};

    switch (operator) {
      case this.constructor.Operators.CONTAINS_ANY.name:
        condition = CaretDelimitedStringQueryBuilder.containsManyQuery(fieldName, operands, '$or', '$like');
        break;
      case this.constructor.Operators.DOES_NOT_CONTAIN_ANY.name:
        condition = CaretDelimitedStringQueryBuilder.notLikeQuery(fieldName, operands);
        break;
      case this.constructor.Operators.CONTAINS_ALL.name:
        condition = CaretDelimitedStringQueryBuilder.containsManyQuery(fieldName, operands, '$and', '$like');
        break;
      case this.constructor.Operators.HAS_NONE.name:
        condition = {
          $or: [
            { [fieldName]: 'null' },
            { [fieldName]: '^^' },
          ],
        };
        break;
      case this.constructor.Operators.HAS_ANY.name:
        condition = {
          $and: [
            { [fieldName]: { $ne: 'null' } },
            { [fieldName]: { $ne: '^^' } },
          ],
        };
        break;
      default:
        // Throw an error or handle the default case
        throw new Error(`Unsupported operator: ${operator}`);
    }

    // Check if the condition is empty
    if ((Array.isArray(condition) && condition.length === 0) ||
      (Object.keys(condition).length === 0)) {
      return null;
    }

    return condition;
  }

  /**
   * Given some plain text search term, surround it
   * it in carets to match the crazy liondesk cache table
   * syntax.
   * Then surround it in % to match anything before or after
   *
   * @param {string} searchTerm
   */
  static caretLikeMatcher(searchTerm) {
    return `%^${searchTerm}^%`;
  }

  /**
   * If not already an array, convert to array.
   *
   * @param {mixed} items
   */
  static convertToArray(items) {
    if (!Array.isArray(items)) {
      items = [items];
    }

    return items;
  }

  /**
   * Construct a contains any query with this format:
   *
   * if only one operand, contains all === contains any:
   *
   *  first_name[$like]=%^help^%
   *  {
   *    first_name: {
   *      $like: '%^help^%
   *    }
   *  }
   *
   * With two or more operands, it's more complicated:
   * {
   *    $or[0][first_name][$like]=%^help^%&$or[1][first_name][$like]=%^me^%
   *    $or: [
   *      {
   *        first_name: {
   *          $like: '%^help^%
   *        },
   *      },
   *      {
   *        first_name: {
   *          $like: '%^me^%
   *        }
   *      }
   *      ...
   *    ]
   * @param {string} fieldName
   * @param {Array} operands - values to match against
   * @param {string} andOr - either '$and' or '$or'
   */
  static containsManyQuery(fieldName, operands, andOr, likeNotLike) {
    operands = this.convertToArray(operands);

    if (operands.length === 0) {
      return {};
    }

    if (operands.length === 1) {
      return this.singleLikeCondition(fieldName, operands[0], likeNotLike);
    }

    const query = {
      [andOr]: [],
    };
    operands.forEach(operand => {
      query[andOr].push({
        [fieldName]: {
          [likeNotLike]: this.caretLikeMatcher(operand),
        },
      });
    });
    return query;
  }

  /** Not like query has to also be compared to null, as ...  */
  static notLikeQuery(fieldName, operands) {
    operands = this.convertToArray(operands);

    if (operands.length === 0) {
      return {};
    }

    if (operands.length === 1) {
      return this.singleNotLikeCondition(fieldName, operands[0]);
    }

    const query = {
      $and: [],
    };
    operands.forEach(operand => {
      query.$and.push(this.singleNotLikeCondition(fieldName, operand));
    });
    return query;
  }

  /**
   * Form a single like condition
   * @param {*} fieldName
   * @param {*} operand
   */
  static singleLikeCondition(fieldName, operand, likeNotLike) {
    return {
      [fieldName]: {
        [likeNotLike]: this.caretLikeMatcher(operand),
      },
    };
  }

  static singleNotLikeCondition(fieldName, operand) {
    return {
      $or: [
        { [fieldName]: 'null' },
        { [fieldName]: { $notlike: this.caretLikeMatcher(operand) } },
      ],
    };
  }

}
